"use client";

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { LOGIN_EVENT, clearJustLoggedIn, peekJustLoggedIn } from '@/lib/loginFlag';

/**
 * The first-run sequence, in one place (owner brief, 2026-08-21).
 *
 * The order the owner specified is: log in, slides, portal door, mandatory
 * permission, optional permissions, then the guided tour. Before this context
 * existed nothing owned that order. The door animation asked sessionStorage
 * directly, the consent dialogs asked only whether onboarding had finished,
 * and the tour asked nobody at all, so on a real first run the door and the
 * consent dialogs both raced the dashboard and the tour never started.
 *
 * This provider owns exactly one fact: how far the entry animation has got.
 * Everything downstream reads `entrySettled` and waits its turn.
 *
 * PHASES
 *   'none'    no login to celebrate (a reload, an internal navigation).
 *   'pending' the user authenticated; the door is owed but has not mounted.
 *   'playing' the door is on screen.
 *   'done'    the door finished, or was never going to play.
 *
 * The pending state deliberately survives client navigation, because the whole
 * point is that the door is owed across the login → onboarding → dashboard
 * hop. It is cleared from sessionStorage only when it is actually spent.
 */

export type DoorPhase = 'none' | 'pending' | 'playing' | 'done';

interface EntryFlowContextValue {
    doorPhase: DoorPhase;
    /** The door is owed and has not been spent. Layout renders it on this. */
    doorOwed: boolean;
    /** Nothing cinematic is on screen or pending; dialogs may take the stage. */
    entrySettled: boolean;
    /** Layout calls this as the animation mounts. */
    beginDoor: () => void;
    /** Layout calls this when the animation reports completion. */
    endDoor: () => void;
}

const EntryFlowContext = createContext<EntryFlowContextValue | undefined>(undefined);

/**
 * Safety net. A pending door that never gets a shell to play in (the profile
 * read failed, the user landed on a route with no Layout, the viewport is
 * mobile) must not hold the consent dialogs hostage forever. Generous enough
 * that a slow profile fetch still gets its animation.
 */
const DOOR_FALLBACK_MS = 6000;

/**
 * Routes that come BEFORE the shell that plays the animation. The door is owed
 * across the whole login → slides → dashboard hop, and a first run spends
 * minutes on the slides, so the safety timer must not be counting down while
 * the user is still reading them: it would spend the animation on a route that
 * cannot render it and the user would never see the door at all.
 */
function isPreShellRoute(pathname: string | null): boolean {
    if (!pathname) return false;
    return (
        pathname === '/' ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/onboarding') ||
        pathname.startsWith('/auth') ||
        pathname.startsWith('/verify-email') ||
        pathname.startsWith('/reset-password')
    );
}

export const EntryFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Lazy initialiser: on a full page load (the OAuth round trip, a reload
    // mid-flow) the flag is already in sessionStorage before React runs.
    const [doorPhase, setDoorPhase] = useState<DoorPhase>(() =>
        peekJustLoggedIn() ? 'pending' : 'none',
    );

    // The login → dashboard hop is a client navigation, so this provider does
    // not remount and the initialiser above has already run. The event is how
    // a login that happens later in the same document is heard.
    useEffect(() => {
        const onLogin = () => setDoorPhase((prev) => (prev === 'none' ? 'pending' : prev));
        window.addEventListener(LOGIN_EVENT, onLogin);
        return () => window.removeEventListener(LOGIN_EVENT, onLogin);
    }, []);

    const pathname = usePathname();
    useEffect(() => {
        if (doorPhase !== 'pending') return;
        if (isPreShellRoute(pathname)) return;
        const timer = window.setTimeout(() => {
            clearJustLoggedIn();
            setDoorPhase('done');
        }, DOOR_FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [doorPhase, pathname]);

    const beginDoor = useCallback(() => {
        setDoorPhase((prev) => (prev === 'pending' ? 'playing' : prev));
    }, []);

    const endDoor = useCallback(() => {
        clearJustLoggedIn();
        setDoorPhase('done');
    }, []);

    const value = useMemo<EntryFlowContextValue>(() => ({
        doorPhase,
        doorOwed: doorPhase === 'pending' || doorPhase === 'playing',
        entrySettled: doorPhase === 'none' || doorPhase === 'done',
        beginDoor,
        endDoor,
    }), [doorPhase, beginDoor, endDoor]);

    return <EntryFlowContext.Provider value={value}>{children}</EntryFlowContext.Provider>;
};

export const useEntryFlow = (): EntryFlowContextValue => {
    const context = useContext(EntryFlowContext);
    if (context === undefined) {
        throw new Error('useEntryFlow must be used within an EntryFlowProvider');
    }
    return context;
};
